// Sticker print button with template picker (single passport).
// Renders inline button; on click downloads the PDF.
import { useState } from "react";
import { api, errorToMessage } from "@/lib/apiClient";

export default function StickerPrintButton({
  passportId,
  source = "INVENTORY",
  variant = "compact",  // "compact" | "full"
  onPrinted,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const doPrint = async (tpl) => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.get(
        `/stickers/${passportId}/single.pdf`,
        { params: { template: tpl, source }, responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      window.open(url, "_blank", "noopener,noreferrer");
      onPrinted && onPrinted(tpl);
      setOpen(false);
    } catch (e) { setError(errorToMessage(e, "Could not generate sticker")); }
    finally { setBusy(false); }
  };

  if (variant === "compact") {
    return (
      <div className="relative inline-block" data-testid={`sticker-btn-${passportId}`}>
        <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                title="Print sticker"
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">
          🏷 Sticker
        </button>
        {open && (
          <div onClick={(e) => e.stopPropagation()}
               className="absolute right-0 z-10 mt-1 w-48 rounded-md border border-slate-200 bg-white p-1 shadow-lg">
            <button type="button" disabled={busy}
                    onClick={() => doPrint("05x15")}
                    className="block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-slate-50"
                    data-testid={`sticker-print-05x15-${passportId}`}>
              0.5 × 1.5 cm (default)
            </button>
            <button type="button" disabled={busy}
                    onClick={() => doPrint("10x20")}
                    className="block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-slate-50"
                    data-testid={`sticker-print-10x20-${passportId}`}>
              1.0 × 2.0 cm
            </button>
            {error && <div className="px-2 py-1 text-[11px] text-rose-700">{error}</div>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid={`sticker-btn-${passportId}`}>
      <button type="button" disabled={busy}
              onClick={() => doPrint("05x15")}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60">
        Print 0.5×1.5 cm
      </button>
      <button type="button" disabled={busy}
              onClick={() => doPrint("10x20")}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
        Print 1×2 cm
      </button>
      {error && <span className="text-xs text-rose-700">{error}</span>}
    </div>
  );
}


export function BulkStickerButton({ passportIds, source = "BULK_INVENTORY" }) {
  const [busy, setBusy] = useState(false);
  const [tpl, setTpl] = useState("05x15");
  const [error, setError] = useState(null);

  const go = async () => {
    if (!passportIds || passportIds.length === 0) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/stickers/bulk.pdf",
        { passport_ids: passportIds, template: tpl, source },
        { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) { setError(errorToMessage(e, "Could not generate bulk stickers")); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2" data-testid="bulk-sticker">
      <select value={tpl} onChange={(e) => setTpl(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-xs">
        <option value="05x15">0.5×1.5 cm</option>
        <option value="10x20">1.0×2.0 cm</option>
      </select>
      <button type="button" disabled={busy || !passportIds?.length} onClick={go}
              data-testid="bulk-sticker-btn"
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60">
        🏷 Print {passportIds?.length || 0} stickers
      </button>
      {error && <span className="text-xs text-rose-700">{error}</span>}
    </div>
  );
}
